import { memo, useEffect, useState, useCallback, type ReactNode } from 'react';
import {
  Box,
  Typography,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { getAllSourcesStatistics, getEsIndexValidationProcessDetails } from '../../api/DeltaForge';
import styles from './styles/DeltaForge.styles';
import {
  DB_SCHEMA_VALIDATION_SOURCE_MAPPING,
  ES_INDEX_VALIDATION_PROCESS_MAPPING
} from './contants';

function missingDataRecordReactKey(
  rec: Record<string, unknown>,
  sourceLabel: string,
  position: number
): string {
  const hash = rec.events_hash ?? rec.eventsHash;
  if (hash != null && String(hash) !== '') {
    return `${sourceLabel}:events_hash:${String(hash)}`;
  }
  const { id } = rec;
  if (id != null && String(id) !== '') {
    return `${sourceLabel}:id:${String(id)}`;
  }
  const url = rec.event_url ?? rec.detail_page_doc_url;
  if (url != null && String(url) !== '') {
    return `${sourceLabel}:url:${String(url)}`;
  }
  const { title } = rec;
  if (title != null && String(title) !== '') {
    return `${sourceLabel}:title:${String(title)}`;
  }
  return `${sourceLabel}:fallback:${position}:${JSON.stringify(rec).slice(0, 80)}`;
}

/** Renders timestamps as `yyyy-MM-dd HH:mm:ss GMT` (UTC wall time). */
function formatDateTimeGmt(value: string | null | undefined): string {
  if (value == null || value === '') return 'NA';
  const trimmed = String(value).trim();
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return trimmed;
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = d.getUTCFullYear();
  const mo = pad(d.getUTCMonth() + 1);
  const da = pad(d.getUTCDate());
  const h = pad(d.getUTCHours());
  const mi = pad(d.getUTCMinutes());
  const s = pad(d.getUTCSeconds());
  return `${y}-${mo}-${da} ${h}:${mi}:${s} GMT`;
}

function formatMissingRecordValue(value: unknown): ReactNode {
  if (value == null) return 'NA';
  if (typeof value === 'object') {
    return (
      <Typography
        component='pre'
        sx={{
          m: 0,
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
        {JSON.stringify(value, null, 2)}
      </Typography>
    );
  }
  return String(value);
}

function normalizeSourceVsRiaValidation(raw: unknown): Record<string, any>[] | null {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    return raw.filter(item => item != null && typeof item === 'object') as Record<string, any>[];
  }
  if (typeof raw === 'object') {
    return [raw as Record<string, any>];
  }
  return null;
}

function sourceVsRiaEntryKey(
  entry: Record<string, any>,
  rowSource: string,
  position: number
): string {
  const sid = entry.source_id;
  const src = entry.source;
  const at = entry.updated_at ?? entry.refresh_date;
  const sub = entry.validation_subkey;
  const subPart = sub != null && String(sub).trim() !== '' ? `:sub:${String(sub).trim()}` : '';
  if (sid != null && String(sid) !== '') {
    return `${rowSource}:source_id:${String(sid)}${subPart}:${String(at ?? '')}`;
  }
  if (src != null && String(src) !== '') {
    return `${rowSource}:entry:${String(src)}${subPart}:${String(at ?? '')}:${position}`;
  }
  return `${rowSource}:entry:${subPart}:${position}`;
}

type SourceVsRiaValidationEntryBlockProps = {
  entry: Record<string, any>;
  rowSource: string;
  // eslint-disable-next-line no-unused-vars -- matches getErrorElement signature from parent
  getErrorElement: (count: unknown) => ReactNode;
};

const SourceVsRiaValidationEntryBlock = memo(function SourceVsRiaValidationEntryBlock({
  entry,
  rowSource,
  getErrorElement
}: SourceVsRiaValidationEntryBlockProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const missingList: Record<string, unknown>[] = Array.isArray(entry.missing_data)
    ? entry.missing_data
    : [];
  const isMissingCountZero = (() => {
    const mc = entry.missing_count;
    if (mc == null || mc === '') return true;
    if (typeof mc === 'number') return mc === 0;
    const n = Number(mc);
    return Number.isFinite(n) && n === 0;
  })();
  const labelSource = entry.source != null ? String(entry.source) : rowSource || 'Source';
  const validationSubkey =
    entry.validation_subkey != null && String(entry.validation_subkey).trim() !== ''
      ? String(entry.validation_subkey).trim()
      : null;
  const titleSuffix =
    entry.source_id != null && String(entry.source_id) !== ''
      ? ` — ${labelSource} (source_id: ${String(entry.source_id)})${
          validationSubkey ? ` · ${validationSubkey}` : ''
        }`
      : ` — ${labelSource}${validationSubkey ? ` · ${validationSubkey}` : ''}`;
  const missingLabelBase = labelSource;

  let missingFooter: ReactNode;
  if (missingList.length > 0) {
    missingFooter = (
      <Button
        size='small'
        variant='outlined'
        onClick={e => {
          e.stopPropagation();
          setModalOpen(true);
        }}>
        View all missing records ({missingList.length})
      </Button>
    );
  } else if (isMissingCountZero) {
    missingFooter = (
      <Typography variant='body2' color='text.secondary'>
        No missing records
      </Typography>
    );
  } else {
    missingFooter = (
      <Typography variant='body2' color='text.secondary'>
        Not integrated
      </Typography>
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ backgroundColor: '#f7f7f7', my: '4px' }}>
        <Table size='small'>
          <TableBody>
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell component='th' scope='row'>
                Source
              </TableCell>
              <TableCell scope='row'>{labelSource}</TableCell>
            </TableRow>
            {validationSubkey != null ? (
              <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell component='th' scope='row'>
                  Sub-Source-Key
                </TableCell>
                <TableCell scope='row'>{validationSubkey}</TableCell>
              </TableRow>
            ) : null}
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell component='th' scope='row'>
                Missing Count
              </TableCell>
              <TableCell scope='row'>
                {typeof entry.missing_count === 'number'
                  ? getErrorElement(entry.missing_count)
                  : entry.missing_count ?? 'NA'}
              </TableCell>
            </TableRow>
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell component='th' scope='row'>
                Expected Count
              </TableCell>
              <TableCell scope='row'>
                {typeof entry.expected_count === 'number'
                  ? getErrorElement(entry.expected_count)
                  : entry.expected_count ?? '--'}
              </TableCell>
            </TableRow>
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell component='th' scope='row'>
                Refresh date
              </TableCell>
              <TableCell scope='row'>{formatDateTimeGmt(entry.refresh_date)}</TableCell>
            </TableRow>
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell component='th' scope='row'>
                Last updated at
              </TableCell>
              <TableCell scope='row'>{formatDateTimeGmt(entry.updated_at)}</TableCell>
            </TableRow>
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell component='th' scope='row'>
                Resolution comment
              </TableCell>
              <TableCell scope='row' sx={{ maxWidth: 220, wordBreak: 'break-word' }}>
                {entry.resolution_comment != null && entry.resolution_comment !== ''
                  ? String(entry.resolution_comment)
                  : 'NA'}
              </TableCell>
            </TableRow>
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell colSpan={2} sx={{ py: 1 }}>
                {missingFooter}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={modalOpen}
        onClose={closeModal}
        maxWidth='md'
        fullWidth
        scroll='paper'
        onClick={e => e.stopPropagation()}>
        <DialogTitle>Missing records{titleSuffix}</DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {missingList.map((rec, idx) => {
            const keys =
              rec && typeof rec === 'object'
                ? Object.keys(rec as object).sort((a, b) => a.localeCompare(b))
                : [];
            const recordKey = missingDataRecordReactKey(
              rec as Record<string, unknown>,
              missingLabelBase,
              idx
            );
            return (
              <Box key={recordKey} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
                <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
                  Record {idx + 1} of {missingList.length}
                </Typography>
                <TableContainer component={Paper} variant='outlined'>
                  <Table size='small'>
                    <TableBody>
                      {keys.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2}>(empty object)</TableCell>
                        </TableRow>
                      ) : (
                        keys.map(key => (
                          <TableRow key={key}>
                            <TableCell
                              component='th'
                              scope='row'
                              sx={{
                                width: '32%',
                                verticalAlign: 'top',
                                fontWeight: 500,
                                wordBreak: 'break-word'
                              }}>
                              {key}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                              {formatMissingRecordValue((rec as Record<string, unknown>)[key])}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} variant='contained' color='primary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

type SourceVsRiaValidationCellProps = {
  row: Record<string, any>;
  // eslint-disable-next-line no-unused-vars -- matches getErrorElement signature from parent
  getErrorElement: (count: unknown) => ReactNode;
};

const SourceVsRiaValidationCell = memo(function SourceVsRiaValidationCell({
  row,
  getErrorElement
}: SourceVsRiaValidationCellProps) {
  const entries = normalizeSourceVsRiaValidation(row?.source_vs_ria_validation);

  if (entries == null) {
    return <Typography sx={{ py: 1 }}>Not integrated</Typography>;
  }
  if (entries.length === 0) {
    return <Typography sx={{ py: 1 }}>No validation entries</Typography>;
  }

  const rowSource = row?.source != null ? String(row.source) : '';

  return (
    <Stack spacing={2} sx={{ py: 0.5 }}>
      {entries.map((entry, idx) => (
        <SourceVsRiaValidationEntryBlock
          key={sourceVsRiaEntryKey(entry, rowSource, idx)}
          entry={entry}
          rowSource={rowSource}
          getErrorElement={getErrorElement}
        />
      ))}
    </Stack>
  );
});

const DeltaForge = () => {
  const [statistics, setStatistics] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const getStatistics = async () => {
      setIsLoading(true);
      try {
        const response = await getAllSourcesStatistics();

        if (response?.status !== 200 || !response?.data?.Success) {
          setStatistics([]);
          return;
        }

        setStatistics(response.data.Success);
      } catch {
        setStatistics([]);
      } finally {
        setIsLoading(false);
      }
    };

    getStatistics();
  }, []);

  const getErrorElement = (error: any) => {
    return (
      <Typography display='inline' sx={{ ...(error && error > 0 && styles.error) }}>
        {error}
      </Typography>
    );
  };

  const esIndexValidationProcessDetails = async (source: string, validationType: string) => {
    try {
      const response = await getEsIndexValidationProcessDetails(source, validationType);
      return response?.status === 200 && Boolean(response?.data?.Success);
    } catch {
      return false;
    }
  };

  const handleEsIndexValidationClick = (source: string, validationType: string) => {
    esIndexValidationProcessDetails(source, validationType).catch(() => undefined);
  };

  const DATAGRID_COLUMNS: GridColDef[] = [
    {
      field: '#',
      headerName: '#',
      width: 25,
      renderCell: params => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: 'source', headerName: 'Source', width: 150 },
    {
      field: 'bastion',
      headerName: 'Invalid Files on Bastion',
      width: 240,
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <TableContainer component={Paper} sx={{ backgroundColor: '#f7f7f7' }}>
            <Table size='small'>
              <TableBody>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component='th' scope='row'>
                    PDF
                  </TableCell>
                  <TableCell scope='row'>{getErrorElement(params?.row?.pdf_files_count)}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component='th' scope='row'>
                    JSON
                  </TableCell>
                  <TableCell scope='row'>
                    {getErrorElement(params?.row?.json_files_count)}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component='th' scope='row'>
                    Others
                  </TableCell>
                  <TableCell scope='row'>
                    {getErrorElement(params?.row?.other_files_count)}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component='th' scope='row'>
                    Total
                  </TableCell>
                  <TableCell scope='row'>{params?.row?.total_files_count ?? 'NA'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        );
      }
    },
    {
      field: 'schema_validation',
      headerName: 'DB Schema Validation',
      width: 200,
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <Link
            href={`/data_quality?schema=${
              DB_SCHEMA_VALIDATION_SOURCE_MAPPING[params?.row?.source?.toLowerCase()]
            }`}
            target='_blank'>
            {params?.row?.source?.toUpperCase() ?? 'Source'}
          </Link>
        );
      }
    },
    {
      field: 'vertex_ai_sync',
      headerName: 'Vertex AI Sync',
      width: 360,
      renderCell: (params: GridRenderCellParams<any>) => {
        const v = params?.row?.vertex_ai_validation;
        if (!v || typeof v !== 'object') {
          return <Typography sx={{ py: 1 }}>Not integrated</Typography>;
        }
        return (
          <TableContainer component={Paper} sx={{ backgroundColor: '#f7f7f7', my: '20px' }}>
            <Table size='small'>
              <TableBody>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component='th' scope='row'>
                    Bucket
                  </TableCell>
                  <TableCell scope='row'>{v.bucket_name ?? 'NA'}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component='th' scope='row'>
                    Source docs
                  </TableCell>
                  <TableCell scope='row'>{v.source_docs ?? 'NA'}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component='th' scope='row'>
                    Indexed docs
                  </TableCell>
                  <TableCell scope='row'>{v.indexed_docs ?? 'NA'}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component='th' scope='row'>
                    Completeness
                  </TableCell>
                  <TableCell scope='row'>
                    {v.completeness_str ?? v.completeness_pct ?? 'NA'}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component='th' scope='row'>
                    Duplicates
                  </TableCell>
                  <TableCell scope='row'>{getErrorElement(v.duplicates)}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component='th' scope='row'>
                    Errors
                  </TableCell>
                  <TableCell scope='row'>{getErrorElement(v.errors)}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component='th' scope='row'>
                    Run at
                  </TableCell>
                  <TableCell scope='row'>{formatDateTimeGmt(v.run_at)}</TableCell>
                </TableRow>
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component='th' scope='row'>
                    Refresh Date
                  </TableCell>
                  <TableCell scope='row'>{v.refresh_date}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        );
      }
    },
    {
      field: 'es_index_validation',
      headerName: 'ES Index Validation',
      width: 250,
      renderCell: (params: GridRenderCellParams<any>) => {
        if (!params?.row?.es_validations) {
          return <Typography>Not integrated</Typography>;
        }
        return (
          <TableContainer component={Paper} sx={{ backgroundColor: '#f7f7f7' }}>
            <Table size='small'>
              <TableBody>
                {params?.row?.es_validations?.map((esValidation: any) => {
                  const typeKey = esValidation.validation_type;
                  const label =
                    (typeKey && ES_INDEX_VALIDATION_PROCESS_MAPPING[typeKey]) ||
                    [esValidation.source, typeKey].filter(Boolean).join(' · ') ||
                    'ES validation';
                  const rowKey = `${params.row.source}:${
                    esValidation.source_id ?? esValidation.source ?? 'src'
                  }:${typeKey ?? 'type'}:${esValidation.total_entries ?? ''}:${
                    esValidation.total_failed_entries ?? ''
                  }`;
                  return (
                    <TableRow
                      key={rowKey}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer' }}
                      onClick={() =>
                        handleEsIndexValidationClick(
                          esValidation.source ?? params.row.source,
                          typeKey ?? ''
                        )
                      }>
                      <TableCell component='th' scope='row'>
                        {label}
                      </TableCell>
                      <TableCell scope='row'>
                        {esValidation.total_failed_entries ?? 0}/{esValidation.total_entries ?? 0}{' '}
                        failed
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        );
      }
    },
    {
      field: 'source_vs_ria_validation',
      headerName: 'Source vs RIA validation',
      width: 400,
      renderCell: (params: GridRenderCellParams<any>) => (
        <SourceVsRiaValidationCell row={params.row} getErrorElement={getErrorElement} />
      )
    },
    {
      field: 's3_validation',
      headerName: 'S3 Validation',
      width: 130,
      renderCell: () => {
        return <Typography>NA</Typography>;
      }
    }
  ];

  return (
    <Box mt={10}>
      <Typography textAlign='center' pb={2}>
        Delta Forge
      </Typography>
      <Box height='80vh' px={3} sx={styles.root}>
        <DataGrid
          rows={statistics}
          columns={DATAGRID_COLUMNS}
          getRowId={row => row?.source}
          style={{ height: '100%' }}
          rowSpacingType='margin'
          density='standard'
          loading={isLoading}
          getRowHeight={() => 'auto'}
        />
      </Box>
    </Box>
  );
};

export default memo(DeltaForge);
